import { Link } from "react-router-dom";
import { Check, Users, Luggage, Snowflake, ArrowRight, Clock, MapPin, Star } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { waLink, img } from "../data/site";
import { useLang } from "../i18n";
import Reveal from "./Reveal";
import * as Icons from "lucide-react";

export const DynIcon = ({ name, size = 20, className = "" }) => {
  const I = Icons[name] || Icons.Car;
  return <I size={size} className={className} />;
};

export function ServiceCard({ s, i = 0 }) {
  return (
    <Reveal delay={(i % 3) * 0.08}>
      <article className="group bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden h-full flex flex-col">
        <div className="relative h-44 overflow-hidden">
          <img src={img(s.img, 700)} alt={`${s.title} — Blue City Taxi Jodhpur`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
          <span className="absolute bottom-3 left-3 w-10 h-10 rounded-xl bg-white/95 flex items-center justify-center text-royal shadow">
            <DynIcon name={s.icon} />
          </span>
        </div>
        <div className="p-5 flex flex-col grow">
          <h3 className="font-heading font-bold text-lg text-navy">{s.title}</h3>
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
          <ul className="mt-3 space-y-1.5">
            {s.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2 text-[13px] text-slate-600">
                <Check size={14} className="text-gold-dark mt-0.5 shrink-0" /> {b}
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2 mt-auto">
            <a href={waLink(`Hello Vikash ji, I want to book: ${s.title}. Please share availability and fare.`)} target="_blank" rel="noopener noreferrer" data-testid={`service-wa-${s.slug}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-wa text-white text-xs font-bold py-2.5 hover:brightness-95 transition">
              <FaWhatsapp size={14} /> WhatsApp
            </a>
            <Link to="/contact" data-testid={`service-book-${s.slug}`} className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-royal text-white text-xs font-bold py-2.5 hover:bg-royal-700 transition-colors">
              {s.cta} <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export function FleetCard({ v, i = 0 }) {
  const { t } = useLang();
  return (
    <Reveal delay={(i % 3) * 0.08}>
      <article className="group bg-white rounded-3xl border border-slate-200/80 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 overflow-hidden">
        <div className="relative h-56 sm:h-64 overflow-hidden bg-royal-50">
          <img src={img(v.img, 800)} alt={`${v.name} taxi for hire in Jodhpur — Blue City Taxi fleet`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/10 to-transparent" />
          <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-navy/85 backdrop-blur border border-gold/40 text-gold font-bold text-xs px-3 py-1.5 shadow-lg">
            <Users size={12} /> {v.passengers}
          </span>
          <div className="absolute bottom-4 left-5 right-5">
            <h3 className="font-heading font-extrabold text-white text-2xl tracking-tight drop-shadow">{v.name}</h3>
            <p className="text-[11px] font-semibold text-white/75 mt-0.5">{v.models}</p>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              [Luggage, v.luggage, t("fLuggage")],
              [Snowflake, v.ac, t("fClimate")],
              [Icons.UserCheck, t("fDriverInc"), t("fDriver")],
            ].map(([I, val, lab], k) => (
              <div key={k} className="rounded-xl bg-royal-50/70 py-2.5 px-1">
                <I size={15} className="mx-auto text-royal" />
                <p className="mt-1 text-[11px] font-bold text-navy leading-tight">{val}</p>
                <p className="text-[10px] text-slate-500">{lab}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[13px] text-slate-600"><span className="font-semibold text-navy">{t("fBestFor")}</span> {v.bestFor}</p>
          <a href={waLink(`Hello Vikash ji, I want to book a ${v.name} (${v.models}). Please share availability and fare.`)} target="_blank" rel="noopener noreferrer" data-testid={`fleet-book-${v.id}`} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-royal text-white text-sm font-bold py-3 hover:bg-royal-700 transition-colors">
            {t("fBook")} <ArrowRight size={15} />
          </a>
        </div>
      </article>
    </Reveal>
  );
}

export function RouteCard({ r, i = 0 }) {
  return (
    <Reveal delay={(i % 4) * 0.06}>
      <article className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-lg hover:border-royal/30 transition-all p-5 h-full flex flex-col">
        <div className="flex items-center gap-2 font-heading font-bold text-navy text-lg">
          {r.from} <ArrowRight size={16} className="text-gold-dark shrink-0" /> {r.to}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold">
          <span className="inline-flex items-center gap-1 rounded-full bg-royal-50 text-royal-700 px-2.5 py-1"><MapPin size={11} /> {r.km}</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-royal-50 text-royal-700 px-2.5 py-1"><Clock size={11} /> {r.time}</span>
        </div>
        <p className="mt-3 text-[13px] text-slate-600">One-way drop — pay for one side only. Clean AC cab with an experienced route driver.</p>
        <a href={waLink(`Hello Vikash ji, I need a one-way taxi from ${r.from} to ${r.to}. Please share the latest fare.`)} target="_blank" rel="noopener noreferrer" data-testid={`route-quote-${r.from}-${r.to}`.toLowerCase().replace(/\s+/g, "-")} className="mt-4 pt-4 border-t border-slate-100 mt-auto inline-flex items-center justify-center gap-2 rounded-lg border-2 border-gold text-gold-dark text-xs font-extrabold py-2.5 hover:bg-gold hover:text-navy transition-colors uppercase tracking-wide">
          Get Latest Fare
        </a>
      </article>
    </Reveal>
  );
}

export function CityCard({ d, i = 0 }) {
  const inner = (
    <article className="group relative rounded-2xl overflow-hidden h-56 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <img src={img(d.img, 600)} alt={`Taxi service in ${d.name}, Rajasthan — Blue City Taxi`} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/25 to-transparent" />
      <div className="absolute bottom-0 p-4 w-full">
        <h3 className="font-heading font-bold text-white text-lg leading-tight">{d.name}</h3>
        <p className="text-[11px] text-white/70 font-medium mt-0.5">{d.known}</p>
        {d.slug && (
          <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-gold">
            Explore taxi service <ArrowRight size={12} />
          </span>
        )}
      </div>
    </article>
  );
  return (
    <Reveal delay={(i % 4) * 0.06}>
      {d.slug ? <Link to={`/taxi-service-${d.slug}`} data-testid={`city-card-${d.slug}`}>{inner}</Link> : inner}
    </Reveal>
  );
}

export function TourCard({ t: tour, i = 0 }) {
  const { t } = useLang();
  return (
    <Reveal delay={(i % 3) * 0.08}>
      <article className="group bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden h-full flex flex-col">
        <div className="relative h-48 overflow-hidden">
          <img src={img(tour.img, 700)} alt={`${tour.name} — Rajasthan tour package by Blue City Taxi`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-gold text-navy text-[10px] font-extrabold uppercase tracking-wider px-3 py-1.5">
            <Clock size={11} /> {tour.duration}
          </span>
          <h3 className="absolute bottom-3 left-4 right-4 font-heading font-bold text-white text-xl">{tour.name}</h3>
        </div>
        <div className="p-5 flex flex-col grow">
          <p className="text-[11px] font-bold uppercase tracking-widest text-royal">{tour.destinations}</p>
          <ul className="mt-3 space-y-1.5">
            {tour.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2 text-[13px] text-slate-600">
                <Star size={13} className="text-gold-dark mt-0.5 shrink-0" /> {h}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[12px] text-slate-500">{t("tourVehicles")}</p>
          <a href={waLink(`Hello Vikash ji, I'm interested in the "${tour.name}" (${tour.duration}). Please share the quote and itinerary.`)} target="_blank" rel="noopener noreferrer" data-testid={`tour-quote-${i}`} className="mt-4 pt-4 border-t border-slate-100 mt-auto inline-flex items-center justify-center gap-2 rounded-xl bg-royal text-white text-sm font-bold py-3 hover:bg-royal-700 transition-colors">
            <FaWhatsapp size={15} /> {t("tourQuote")}
          </a>
        </div>
      </article>
    </Reveal>
  );
}

export function SectionHead({ eyebrow, title, text, center = true }) {
  return (
    <Reveal className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold-dark">{eyebrow}</p>
      <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">{title}</h2>
      {text && <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">{text}</p>}
      <div className={`gold-line w-24 mt-5 ${center ? "mx-auto" : ""}`} />
    </Reveal>
  );
}
